import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExam, EXAM_CONFIG } from '@/context/ExamContext';
import { useAuth } from '@/auth/AuthContext';
import { useWhiteLabel } from '@/contexts/WhiteLabelContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Crown, ChevronRight, Lock } from 'lucide-react';

/**
 * ExamSelectorPage - Netflix-style exam selection screen
 * Shows after login for users to choose their target exam
 * Note: White-label institute users skip this page entirely
 */
const ExamSelectorPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectExam, selectedExam } = useExam();
  const { isWhiteLabel, config } = useWhiteLabel();

  // Redirect white-label users directly to dashboard (they don't need to select exam)
  useEffect(() => {
    if (isWhiteLabel && config && user) {
      // Auto-select UPSC for institute users
      localStorage.setItem(`clarias_exam_${user.id}`, 'UPSC');
      localStorage.setItem(`clarias_exam_selector_v2_${user.id}`, 'true');
      selectExam('UPSC');
      navigate('/dashboard', { replace: true });
    }
  }, [isWhiteLabel, config, user, selectExam, navigate]);

  const handleExamSelect = (examId) => {
    const exam = EXAM_CONFIG[examId];
    if (!exam || !exam.active) return;
    
    selectExam(examId);
    
    // Navigate to exam-specific dashboard
    if (examId === 'TGPSC') {
      navigate('/tgpsc');
    } else if (examId === 'APPSC') {
      navigate('/appsc');
    } else if (examId === 'BANKING') {
      navigate('/banking');
    } else {
      navigate('/dashboard');
    }
  };

  // Don't render for white-label users
  if (isWhiteLabel && config) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-white">Redirecting...</div>
    </div>;
  }

  // Temporarily hidden from selector (can be re-added later): TGPSC, APPSC.
  const HIDDEN_EXAMS = ['TGPSC', 'APPSC'];
  const exams = Object.values(EXAM_CONFIG).filter((e) => !HIDDEN_EXAMS.includes(e.id));

  // Get institute branding if white-label
  const instituteName = isWhiteLabel && config ? config.name : null;
  const instituteLogo = isWhiteLabel && config ? config.logo_url : null;
  const primaryColor = isWhiteLabel && config ? config.primary_color || '#FF6B00' : '#FF6B00';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
      {/* Logo - Dynamic based on white-label */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          {isWhiteLabel && config ? (
            // White-label institute branding
            <>
              {instituteLogo ? (
                <img src={instituteLogo} alt={instituteName} className="h-14 object-contain" />
              ) : (
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${config.secondary_color || '#1E3A5F'})` }}
                >
                  <span className="text-white text-2xl font-bold">{instituteName?.charAt(0) || 'I'}</span>
                </div>
              )}
              <h1 className="text-4xl font-bold text-white">{instituteName}</h1>
            </>
          ) : (
            // Default CLARIAS branding
            <>
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white text-2xl font-bold">C</span>
              </div>
              <h1 className="text-4xl font-bold text-white">
                <span className="text-slate-300">CLAR</span>
                <span className="text-orange-500">IAS</span>
              </h1>
            </>
          )}
        </div>
        <p className="text-slate-400 text-lg">
          {isWhiteLabel && config ? 'UPSC Preparation' : 'Clarity over chaos'}
        </p>
      </div>

      {/* Welcome Message */}
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Welcome{user?.name ? `, ${user.name}` : ''}! 👋
        </h2>
        <p className="text-slate-400 text-lg">Which exam are you preparing for?</p>
      </div>

      {/* Exam Cards - Netflix Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl w-full">
        {exams.map((exam) => (
          <Card
            key={exam.id}
            onClick={() => handleExamSelect(exam.id)}
            className={`relative overflow-hidden cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border-2 ${
              !exam.active
                ? 'opacity-60 cursor-not-allowed border-slate-700 bg-slate-800/50' 
                : selectedExam === exam.id
                  ? 'border-white bg-slate-800 ring-2 ring-white/50'
                  : 'border-slate-700 bg-slate-800/80 hover:border-white/50'
            }`}
            data-testid={`exam-card-${exam.id.toLowerCase()}`}
          >
            {/* Gradient Header */}
            <div className={`h-32 bg-gradient-to-br ${exam.color} relative`}>
              {/* Icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-6xl">{exam.icon}</span>
              </div>
              
              {/* Coming Soon Badge */}
              {!exam.active && (
                <div className="absolute top-3 right-3">
                  <Badge className="bg-slate-900/80 text-white border-0">
                    <Lock className="w-3 h-3 mr-1" />
                    Coming Soon
                  </Badge>
                </div>
              )}
              
              {/* Selected Badge */}
              {selectedExam === exam.id && (
                <div className="absolute top-3 left-3">
                  <Badge className="bg-white text-slate-900 border-0">
                    ✓ Selected
                  </Badge>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-5">
              <h3 className="text-xl font-bold text-white mb-1">{exam.name}</h3>
              <p className="text-slate-400 text-sm mb-4">{exam.description}</p>
              
              {/* Stages */}
              <div className="flex items-center gap-2 mb-4">
                {exam.stages.map((stage, idx) => (
                  <React.Fragment key={stage}>
                    <span className="text-xs text-slate-500">{stage}</span>
                    {idx < exam.stages.length - 1 && (
                      <ChevronRight className="w-3 h-3 text-slate-600" />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* CTA */}
              {!exam.active ? (
                <div className="text-center py-2 bg-slate-700/50 rounded-lg text-slate-500 text-sm">
                  Launching Soon
                </div>
              ) : (
                <div className={`text-center py-2 bg-gradient-to-r ${exam.color} rounded-lg text-white font-medium flex items-center justify-center gap-2`}>
                  {selectedExam === exam.id ? 'Continue Preparing' : 'Start Preparing'}
                  <ChevronRight className="w-4 h-4" />
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Footer Note */}
      <p className="text-slate-500 text-sm mt-10 text-center max-w-xl">
        You can switch between exams anytime from your dashboard. 
        Each exam has separate subscription and progress tracking.
      </p>

      {/* PRO Badge Info */}
      <div className="mt-6 flex items-center gap-2 text-slate-400 text-sm">
        <Crown className="w-4 h-4 text-amber-500" />
        <span>Separate PRO subscription for each exam</span>
      </div>
    </div>
  );
};

export default ExamSelectorPage;
